import { Button, Space, Table, Typography, Flex } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams, useLocation } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteStandingsConfiguration } from "../../entities/api";
import { useFetchStandingsConfigurationsPagination } from "../../entities/api/useStandingsConfigurationsAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";
import SearchFilter from "~components/SearchFilter/SearchFilter.js";

const PermissionName = PermissionNames.STANDING_CONFIGURATION;

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameLocal",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link
              to={`/${customerId}/admin/${sport}/${organizationId}/standings-configuration/manage/${record.standingConfigurationId}/`}
            >
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`}/>
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const StandingsConfigurationsList = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useParams();
  const location = useLocation();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(20);
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const { mutate: deleteFn, isPending } = useDeleteStandingsConfiguration();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const { data: standingConfigurationData, isLoading } = useFetchStandingsConfigurationsPagination({
    limit: pageSize,
    offset: offset,
    ...(searchText && { nameLocalContains: searchText }),
  });

  const data = useMemo(() => standingConfigurationData?.data || [], [standingConfigurationData?.data]);
  const nextLink = useMemo(() => standingConfigurationData?.links?.next, [standingConfigurationData?.links?.next]);

  useEffect(() => {
    setFilteredData(data);
  }, [data, setFilteredData]);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.standingConfigurationId);
  }, [deleteFn, selected?.standingConfigurationId]);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title={formatMessage({id: 'standings.configuration.delete'})}
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText={formatMessage({id: 'delete'})}
            okButtonProps={{
              danger: true,
            }}
          >
            <div>
              {formatMessage(
                {
                  id: "delete.confirm",
                },
                { item: selected.nameLocal },
              )}
            </div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>{formatMessage({ id: "standing.configurations" })}</Typography.Title>
        <Space>
          <SearchFilter searchValue={searchText} doSearch={setSearchText} />
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/standings-configuration/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                {formatMessage({id: "standings.configuration"})}
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={columnsone({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
        })}
        rowKey={"standingConfigurationId"}
        dataSource={filteredData}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + data.length + 1 : data.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
          ),
        }}
      />
    </Flex>
  );
};

export default StandingsConfigurationsList;
